import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { getUserProfile, UserProfile } from "../../API/UserProfileApi/GetUserProfile";
import { Address } from "../../API/Address/ViewAllAddedAddressByUserAsync";
import { useLocalization } from "../Context/MultiLanguageContext";
import Playstore from "../../assets/images/playstore.svg";
import Appstore from "../../assets/images/appstore.svg";
import logo from '../../assets/images/pam-web-logo.svg';
import LoginPopupImage from "../../assets/images/loginPopup.png";
import { updateLocalStorageSearchHistory } from "../../utils/updateLocalStorageSearchHistory";
import { RemoveFromFavouritesAsync } from "../../API/AddressFavourites/RemoveFromFavouritesAsync";
import { AddtoFavouritesAsync } from "../../API/AddressFavourites/AddToFavouritesAsync";
import { getUserIdFromToken } from "../../auth/authUtils";
import { fetchAddressResultsAsync } from "../../API/Address/SearchAddressAsync";
import { ShareLink } from "../../API/Address/SharePost";
import Header from "../Header/Header";
import AddressContent from "./AddressContent";
import { shareEncryptedId } from "../../API/Address/ShareGet";

interface SearchHistoryItem {
    query: string;
    results: any;
    outsideBlock?: boolean;
}

interface AddressViewProps {
    updateUserName: (newUserName: string) => void;
    showSidebar?: boolean;
    showHeader?: boolean;
    pamCode?: string;
    encryptedId?: string;
}

type OS = "android" | "ios" | "web";

function getOS(): OS {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    if (/android/i.test(userAgent)) return "android";
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) return "ios";
    return "web";
}

const AddressView: React.FC<AddressViewProps> = ({
    updateUserName,
    showSidebar = true,
    showHeader = true,
    pamCode: propPamCode,
    encryptedId: propEncryptedId
}) => {
    const { pamCode: paramPamCode } = useParams<{ pamCode: string }>();
    const navigate = useNavigate();
    const { t } = useLocalization();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const queryEncryptedId = searchParams.get('s');

    const pamCode = propPamCode || paramPamCode;
    const encryptedId = propEncryptedId || queryEncryptedId;
    const stateAddress = (location.state as { address?: Address })?.address;

    const [addressResults, setAddressResults] = useState<Address[]>([]);
    const [recentSearchHistory, setRecentSearchHistory] = useState<SearchHistoryItem[]>([]);
    const [addresses, setAddresses] = useState<any[]>([]);
    const [address, setAddress] = useState<Address | null>(stateAddress || null);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [copiedPamCode, setCopiedPamCode] = useState<string | null>(null);
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [userName, setUserName] = useState('');
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [showMobileAppPopup, setShowMobileAppPopup] = useState(false);
    const [showQRCodes, setShowQRCodes] = useState(false);
    const [shareUrls, setShareUrls] = useState<{ [key: string]: { url: string; referralLink?: string } }>({});
    const [isLoadingShare, setIsLoadingShare] = useState<{ [key: string]: boolean }>({});
    const [currentOS, setCurrentOS] = useState<OS>("web");
    const [showLoginPing, setShowLoginPing] = useState(false);

    const hasFetchedRef = useRef(false);
    const currentPamCodeRef = useRef<string>("");

    useEffect(() => {
        setCurrentOS(getOS());
    }, []);

    const isAuthenticated = useCallback(() => {
        const token = localStorage.getItem("token") || localStorage.getItem("authToken");
        return !!token;
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            const mobileAppTimer = setTimeout(() => {
                setShowMobileAppPopup(true);
            }, 15000);

            return () => {
                clearTimeout(mobileAppTimer);
            };
        }
    }, [isAuthenticated]);

    const generateQRCode = useCallback((text: string) => {
        const encodedText = encodeURIComponent(text);
        return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedText}`;
    }, []);

    const handleDownload = useCallback(() => {
        const playStoreLink = "https://play.google.com/store/apps/details?id=vofox.getmethere.pam";
        const appStoreLink = "https://apps.apple.com/in/app/postal-address-map/id6739284153";

        if (currentOS === "android") {
            window.open(playStoreLink, '_blank');
        } else if (currentOS === "ios") {
            window.open(appStoreLink, '_blank');
        } else {
            setShowQRCodes(true);
            setShowMobileAppPopup(true);
        }
    }, [currentOS]);

    const fetchAddressFromPamCode = useCallback(async (pamCodeParam: string) => {
        if (hasFetchedRef.current && currentPamCodeRef.current === pamCodeParam) {
            return;
        }

        const token = localStorage.getItem("token");

        if (!token && showHeader) {
            setErrorMessage(t("sessionExpired") || "Session expired");
            navigate("/");
            return;
        }

        hasFetchedRef.current = true;
        currentPamCodeRef.current = pamCodeParam;

        setIsLoading(true);
        setErrorMessage("");

        try {
            const normalizedPamCode = pamCodeParam.replace(/-/g, '');
            const data = await fetchAddressResultsAsync(normalizedPamCode, token || '');

            if (data) {
                let foundAddress = null;

                if (Array.isArray(data) && data.length > 0) {
                    foundAddress = data[0];
                } else if (data.addresses && Array.isArray(data.addresses) && data.addresses.length > 0) {
                    foundAddress = data.addresses[0];
                }

                if (foundAddress) {
                    setAddress(foundAddress);

                    if (token) {
                        const userId = getUserIdFromToken();
                        if (userId) {
                            const searchHistory = {
                                query: normalizedPamCode,
                                results: data,
                                timestamp: Date.now()
                            };

                            const existingHistory = localStorage.getItem(`recentSearch_${userId}`);
                            let history = existingHistory ? JSON.parse(existingHistory) : [];

                            history = history.filter((item: any) => item.query !== normalizedPamCode);
                            history.unshift(searchHistory);
                            history = history.slice(0, 10);

                            localStorage.setItem(`recentSearch_${userId}`, JSON.stringify(history));
                        }
                    }
                } else {
                    setAddress(null);
                    setErrorMessage("");
                }
            } else {
                setAddress(null);
                setErrorMessage("");
                if (navigator.onLine) {
                    toast.error(t("addressNotFound") || "No address found for this PAM code");
                }
            }
        } catch (error: any) {
            console.error('💥 Error fetching address:', error);
            setErrorMessage(error.message || t("unexpectedError") || "Unexpected error occurred");
        } finally {
            setIsLoading(false);
        }
    }, [t, showHeader, navigate]);

    const fetchAddressFromEncryptedId = useCallback(async (pamCodeParam: string, encryptedIdParam: string) => {
        if (hasFetchedRef.current && currentPamCodeRef.current === encryptedIdParam) {
            return;
        }

        hasFetchedRef.current = true;
        currentPamCodeRef.current = encryptedIdParam;

        setIsLoading(true);
        setErrorMessage("");

        try {
            const token = localStorage.getItem("token") || localStorage.getItem("authToken");
            const data = await shareEncryptedId(pamCodeParam, encryptedIdParam, token, t);

            if (data) {
                setAddress(data);

                if (token) {
                    const userId = getUserIdFromToken();
                    if (userId) {
                        const searchHistory = {
                            query: encryptedIdParam,
                            results: data,
                            timestamp: Date.now()
                        };

                        const existingHistory = localStorage.getItem(`recentSearch_${userId}`);
                        let history = existingHistory ? JSON.parse(existingHistory) : [];

                        history = history.filter((item: any) => item.query !== encryptedIdParam);
                        history.unshift(searchHistory);
                        history = history.slice(0, 10);

                        localStorage.setItem(`recentSearch_${userId}`, JSON.stringify(history));
                    }
                }
            } else {
                setAddress(null);
                setErrorMessage("");
            }
        } catch (error: any) {
            console.error('💥 Error fetching address:', error);
            setErrorMessage(error.message || t("unexpectedError") || "Unexpected error occurred");
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (encryptedId && pamCode) {
            if (encryptedId !== currentPamCodeRef.current) {
                hasFetchedRef.current = false;
            }

            if (!stateAddress && !hasFetchedRef.current) {
                fetchAddressFromEncryptedId(pamCode, encryptedId);
            }
        } else if (pamCode && !encryptedId) {
            if (pamCode !== currentPamCodeRef.current) {
                hasFetchedRef.current = false;
            }

            if (!stateAddress && !hasFetchedRef.current) {
                fetchAddressFromPamCode(pamCode);
            }
        }
    }, [pamCode, encryptedId, stateAddress, fetchAddressFromEncryptedId, fetchAddressFromPamCode]);

    useEffect(() => {
        const storedUserName = localStorage.getItem('userName');
        if (storedUserName) {
            setUserName(storedUserName);
        }
    }, []);

    useEffect(() => {
        if (!address && !stateAddress && !isLoading && (pamCode || encryptedId) && hasFetchedRef.current) {
            setErrorMessage(t("addressdataNotAvailable") || "Address data not available");
        }
    }, [address, stateAddress, isLoading, pamCode, encryptedId, t]);

    const revertFavouriteState = useCallback((addressId: number, originalState: boolean) => {
        setAddress(prev =>
            prev && Number(prev.id) === addressId ? { ...prev, isFavourite: originalState } : prev
        );
    }, []);

    const HeartClick = useCallback(async (addressId: number, isFavourite: boolean) => {
        if (!isAuthenticated()) {
            setShowLoginPopup(true);
            setShowLoginPing(true);
            setTimeout(() => {
                setShowLoginPing(false);
                setShowLoginPopup(false);
            }, 4000);
            return;
        }

        setAddress(prev =>
            prev && Number(prev.id) === addressId ? { ...prev, isFavourite: !prev.isFavourite } : prev
        );

        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            if (!isFavourite) {
                await AddtoFavouritesAsync(addressId, token);
                updateLocalStorageSearchHistory(addressId, true);
            } else {
                await RemoveFromFavouritesAsync(addressId, token);
                updateLocalStorageSearchHistory(addressId, false);
            }
        } catch (error) {
            revertFavouriteState(addressId, isFavourite);
        }
    }, [isAuthenticated, revertFavouriteState]);

    const fetchAndHandleUserProfile = useCallback(async () => {
        try {
            const data = await getUserProfile(t);
            setUserProfile(data);
            setUserName(data.userName);
            updateUserName(data.userName);
            navigate('/profile', {
                state: {
                    userName: data.userName,
                    email: data.email,
                    phoneNumber: data.phoneNumber,
                },
            });
        } catch (error) {
            toast.error(t("sessionExpired"));
            navigate('/home');
        }
    }, [t, updateUserName, navigate]);

    const formatPamCode = useCallback((address: Address): string => {
        if (!address) return "N/A";

        const pin = address.pinCode?.toString().trim() || "";
        const block = address.blockCode?.toString().trim() || "";
        const addr = address.addressCode?.toString().trim() || "";

        if (!pin || !block || !addr) {
            return "N/A";
        }

        return `${pin}-${block}-${addr}`;
    }, []);

    const formatPamCodeForCopy = useCallback((address: Address): string => {
        if (!address) return "";

        const pin = address.pinCode?.toString().trim() || "";
        const block = address.blockCode?.toString().trim() || "";
        const addr = address.addressCode?.toString().trim() || "";

        if (!pin || !block || !addr) {
            return "";
        }

        return `${pin}-${block}-${addr}`;
    }, []);

    const handleLocationIconClick = useCallback((latitude: number, longitude: number) => {
        const defaultMap = localStorage.getItem("defaultMap") || "google";

        if (defaultMap === "waze") {
            const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
            window.open(wazeMapsUrl, '_blank');
        } else if (defaultMap === "mapindia") {
            const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
            window.open(mapplsUrl, "_blank");
        } else if (defaultMap === "apple") {
            const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
            window.open(appleMapsUrl, "_blank");
        } else {
            const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
            window.open(googleMapsUrl, '_blank');
        }
    }, []);

    const handleShareClick = useCallback(async () => {
        if (showShareOptions) {
            setShowShareOptions(false);
            return;
        }

        if (!address?.id) {
            toast.error(t("addressNotFound"));
            return;
        }

        setIsLoadingShare(prev => ({ ...prev, [address.id]: true }));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error(t("sessionExpired"));
                navigate('/');
                return;
            }

            const shareResponse = await ShareLink(parseInt(address.id), token);

            if (shareResponse && shareResponse.url) {
                setShareUrls(prev => ({
                    ...prev,
                    [address.id]: {
                        url: shareResponse.url,
                        referralLink: shareResponse.referralLink
                    }
                }));
                setShowShareOptions(true);
            } else {
                toast.error(t("errorGeneratingShareLink"));
            }
        } catch (error) {
            console.error('Error calling share API:', error);
            toast.error(t("errorGeneratingShareLink"));
        } finally {
            setIsLoadingShare(prev => ({ ...prev, [address.id]: false }));
        }
    }, [showShareOptions, address, t, navigate]);

    const handleCopy = useCallback(async (value: string, type: "pamCode" | "digiPin") => {
        if (!value) return;
        try {
            const finalValue = String(value ?? "").trim();
            await navigator.clipboard.writeText(finalValue);
            setCopiedField({ type, value: finalValue });
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
        }
    }, []);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('userToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
        sessionStorage.clear();
        navigate("/", { replace: true });
    }, [navigate]);

    const shareMessage = useCallback((address: Address) => {
        const shareData = shareUrls[address.id];
        const dynamicUrl = shareData?.url;
        const referralLink = shareData?.referralLink;
        const fallbackUrl = (pamCode && encryptedId)
            ? `https://app.getmethere.in/${pamCode}?s=${encryptedId}`
            : pamCode
                ? `https://app.getmethere.in/${pamCode}`
                : `https://app.getmethere.in/${formatPamCodeForCopy(address)}`;

        const addressLink = dynamicUrl || fallbackUrl;

        const isPreprod = process.env.REACT_APP_API_BASE_URL === "https://preprod-api.getmethere.in";
        const firstName = (userName || "").trim().split(/\s+/)[0] || "";
        const userId = getUserIdFromToken();
        const userRefLink = referralLink
            ? referralLink
            : (isPreprod
                ? `https://preprod.getmethere.in/?ref=${userId || ""}&by=${firstName}`
                : `https://getmethere.in/?ref=${userId || ""}&by=${firstName}`);

        return `Check out this address:
    ${address?.addressLine1}, ${address?.addressLine2}, ${address?.addressLine3}
    PAM Code: ${formatPamCode(address)}
    Digi Pin: ${address?.digiPin}
    Address Link: ${addressLink}

    More details: ${userRefLink}`;
    }, [shareUrls, formatPamCode, formatPamCodeForCopy, pamCode, encryptedId, userName]);

    const handleLoginPopupClose = useCallback(() => {
        setShowLoginPopup(false);
    }, []);

    const handleMobileAppPopupClose = useCallback(() => {
        setShowMobileAppPopup(false);
        setShowQRCodes(false);
    }, []);

    const handleLoginNavigate = useCallback(() => {
        if (pamCode && encryptedId) {
            localStorage.setItem("redirectUrl", `/address/${pamCode}?s=${encryptedId}`);
        } else if (pamCode) {
            localStorage.setItem("redirectUrl", `/address/${pamCode}`);
        }
        navigate("/");
    }, [navigate, pamCode, encryptedId]);

    const handleDownloadNowClick = useCallback(() => {
        setShowQRCodes(true);
    }, []);

    const handleBackToPopup = useCallback(() => {
        setShowQRCodes(false);
    }, []);

    const LoginPopup = useCallback(() => {
        if (!showLoginPopup) return null;

        return (
            <div className={`fixed top-8 left-8 z-[100000] bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm animate-slide-in-left 
                ${showLoginPing ? 'ring-4 ring-sky-300 ring-opacity-75 animate-pulse' : ''}`}>
                <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center">
                        <div className="relative flex size-3 mr-2">
                            <span className={`absolute inline-flex h-full w-full ${showLoginPing ? 'animate-ping' : ''} rounded-full bg-sky-400 opacity-75`}></span>
                            <span className={`relative inline-flex size-3 rounded-full bg-blue-500`}></span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-800">Login Required!</h3>
                    </div>
                    <button
                        onClick={handleLoginPopupClose}
                        className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                        aria-label="Close popup"
                    >
                        ×
                    </button>
                </div>
                <p className="text-sm text-gray-600">
                    Please login to add addresses to favourites and access more features!
                </p>
                <div className="flex gap-2 mt-3">
                    <button
                        onClick={handleLoginNavigate}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium"
                    >
                        Login Now
                    </button>
                </div>
            </div>
        );
    }, [showLoginPopup, showLoginPing, handleLoginPopupClose, handleLoginNavigate]);

    const MobileAppPopup = useCallback(() => {
        if (!showMobileAppPopup) {
            return null;
        }

        const playStoreLink = "https://play.google.com/store/apps/details?id=vofox.getmethere.pam";
        const appStoreLink = "https://apps.apple.com/in/app/postal-address-map/id6739284153";

        if (showQRCodes) {
            return (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-[99998] sm:hidden"></div>

                    <div className="fixed inset-4 sm:top-8 sm:right-8 sm:left-auto sm:inset-auto flex items-center justify-center sm:block z-[99999] sm:bg-transparent">
                        <div className="bg-white border border-gray-200 rounded-2xl shadow-xl px-4 sm:px-6 py-4 w-full max-w-sm sm:w-[300px] h-auto sm:h-[550px] sm:max-w-[320px] animate-slide-in-right max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                                    Download Our App
                                </h3>
                                <button
                                    onClick={handleMobileAppPopupClose}
                                    className="text-gray-400 hover:text-gray-600 text-xl sm:text-lg leading-none transition-colors hover:scale-110 p-1"
                                    aria-label="Close popup"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="space-y-4 sm:space-y-6">
                                <p className="text-xs sm:text-[13px] text-gray-700 mb-2 text-center">
                                    Scan the QR code with your mobile device:
                                </p>

                                <div className="flex flex-row sm:flex-col gap-4 sm:gap-6 justify-center">
                                    <div className="text-center flex-1 sm:flex-none">
                                        <div className="flex items-center justify-center mb-2">
                                            <img src={Playstore} alt="Play Store" className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                                            <h4 className="text-xs font-medium text-gray-700">
                                                Play Store
                                            </h4>
                                        </div>
                                        <div className="bg-gray-50 p-2 rounded-lg border">
                                            <img
                                                src={generateQRCode(playStoreLink)}
                                                alt="Play Store QR Code"
                                                className="w-20 h-20 sm:w-24 sm:h-24 mx-auto"
                                            />
                                        </div>
                                        <button
                                            onClick={() => window.open(playStoreLink, '_blank')}
                                            className="mt-2 text-[10px] sm:text-[11px] text-blue-600 hover:text-blue-700 underline block w-full"
                                        >
                                            Open in Play Store
                                        </button>
                                    </div>

                                    <div className="text-center flex-1 sm:flex-none">
                                        <div className="flex items-center justify-center mb-2">
                                            <img src={Appstore} alt="App Store" className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                                            <h4 className="text-xs font-medium text-gray-700">
                                                App Store
                                            </h4>
                                        </div>
                                        <div className="bg-gray-50 p-2 rounded-lg border">
                                            <img
                                                src={generateQRCode(appStoreLink)}
                                                alt="App Store QR Code"
                                                className="w-20 h-20 sm:w-24 sm:h-24 mx-auto"
                                            />
                                        </div>
                                        <button
                                            onClick={() => window.open(appStoreLink, '_blank')}
                                            className="mt-2 text-[10px] sm:text-[11px] text-blue-600 hover:text-blue-700 underline block w-full"
                                        >
                                            Open in App Store
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 text-center">
                                <button
                                    onClick={handleBackToPopup}
                                    className="text-gray-500 text-xs hover:text-gray-700 transition-colors underline"
                                >
                                    ← Back
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] sm:hidden"></div>

                <div className="fixed inset-4 sm:top-8 sm:right-8 sm:left-auto sm:inset-auto flex items-center justify-center sm:block z-[100000] sm:bg-transparent">
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-xl px-4 sm:px-6 py-2 w-full max-w-sm sm:w-[300px] h-auto sm:h-[550px] sm:max-w-[320px] animate-slide-in-right max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-base sm:text-base font-semibold text-gray-800">
                                Get the Mobile App
                            </h3>
                            <button
                                onClick={handleMobileAppPopupClose}
                                className="text-gray-400 hover:text-gray-600 text-2xl sm:text-lg leading-none transition-colors hover:scale-110 p-1"
                                aria-label="Close popup"
                            >
                                ×
                            </button>
                        </div>

                        <div className="mb-4 text-center">
                            <img
                                src={LoginPopupImage}
                                alt="Mobile App Preview"
                                className="w-36 sm:w-40 rounded-lg shadow-md mx-auto"
                            />
                        </div>

                        <p className="text-sm sm:text-sm text-gray-600 mb-2 text-center leading-relaxed">
                            Access PAM anytime, anywhere. Download our mobile app for a fast and smooth experience
                            {currentOS !== "web" && ` on your ${currentOS === "ios" ? "iPhone/iPad" : "Android device"}`}.
                        </p>

                        <div className="flex flex-col gap-3 sm:gap-2">
                            {currentOS === "ios" ? (
                                <button
                                    onClick={() => window.open(appStoreLink, '_blank')}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-4 sm:py-2 rounded-md text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 w-full"
                                >
                                    Download from App Store
                                </button>
                            ) : currentOS === "android" ? (
                                <button
                                    onClick={() => window.open(playStoreLink, '_blank')}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-4 sm:py-2 rounded-md text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 w-full"
                                >
                                    Download from Play Store
                                </button>
                            ) : (
                                <button
                                    onClick={handleDownloadNowClick}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 sm:py-2 rounded-md text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 w-full"
                                >
                                    Download Now
                                </button>
                            )}

                            <button
                                onClick={handleMobileAppPopupClose}
                                className="text-gray-500 text-sm hover:text-gray-700 transition-colors text-center py-3 sm:py-1 border border-gray-200 rounded-md hover:bg-gray-50"
                            >
                                Maybe Later
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }, [showMobileAppPopup, showQRCodes, currentOS, generateQRCode, handleMobileAppPopupClose, handleDownloadNowClick, handleBackToPopup]);

    const customStyles = `
        body {
            overflow-x: hidden;
        }
        
        .main-container {
            max-width: 100vw;
            overflow-x: hidden;
        }
        
        .animation-delay-500 {
            animation-delay: 0.5s;
        }
        
        .animation-delay-1000 {
            animation-delay: 1s;
        }
        
        .animation-delay-1500 {
            animation-delay: 1.5s;
        }
        
        @keyframes slide-in-right {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }
        
        @keyframes slide-in-left {
            from {
                transform: translateX(-100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }
        
        @media (max-width: 640px) {
            @keyframes slide-in-right {
                from {
                    transform: translateY(30px) scale(0.9);
                    opacity: 0;
                }
                to {
                    transform: translateY(0) scale(1);
                    opacity: 1;
                }
            }
            
            body.popup-open {
                overflow: hidden;
            }
        }
        
        .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out forwards;
        }
        
        .animate-slide-in-left {
            animation: slide-in-left 0.4s ease-out forwards;
        }
        
        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
        
        .bg-black.bg-opacity-50 {
            animation: fadeIn 0.2s ease-out;
        }
        
        @keyframes slide-out-right {
            from {
                transform: translateX(0);
                opacity: 1;
            }
            to {
                transform: translateX(100%);
                opacity: 0;
            }
        }
        
        .animate-slide-out-right {
            animation: slide-out-right 0.3s ease-in forwards;
        }
    `;

    if (showSidebar && isAuthenticated()) {
        return (
            <div className="min-h-screen bg-gray-100 overflow-x-hidden">
                <style>{customStyles}</style>
                <ToastContainer />

                <Header
                    userName={userName}
                    fetchUserProfile={fetchAndHandleUserProfile}
                    handleLogout={handleLogout}
                />

                <AddressContent
                    address={address}
                    isLoading={isLoading}
                    successMessage={successMessage}
                    showShareOptions={showShareOptions}
                    copiedField={copiedField}
                    currentOS={currentOS}
                    isAuthenticated={isAuthenticated}
                    handleDownload={handleDownload}
                    formatPamCode={formatPamCode}
                    formatPamCodeForCopy={formatPamCodeForCopy}
                    handleCopy={handleCopy}
                    HeartClick={HeartClick}
                    handleShareClick={handleShareClick}
                    handleLocationIconClick={handleLocationIconClick}
                    shareMessage={shareMessage}
                    t={t}
                />

                <footer className="w-full mt-4 sm:mt-auto px-4">
                    <div className="w-full px-6 py-6 bg-white shadow-shadow1 rounded-md">
                        <p className="text-center text-jetgrey text-sm">
                            GET ME THERE © {new Date().getFullYear()}
                        </p>
                    </div>
                </footer>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 relative overflow-x-hidden">
            <style>{customStyles}</style>
            <ToastContainer />

            <LoginPopup />
            <MobileAppPopup />

            <header className="flex items-center justify-between px-6 py-4 bg-white shadow-md fixed top-0 left-0 right-0 z-[10000]">
                <div className="flex items-center gap-3">
                    <img src={logo} alt="Logo" className="h-12 w-auto" />
                </div>
                <button
                    onClick={handleLoginNavigate}
                    className="relative border border-gray-200 focus:outline-none flex rounded-full h-12 items-center px-2 gap-2"
                >
                    <div className="w-8 h-8 overflow-hidden rounded-full">
                        <img
                            className="object-cover w-full h-full"
                            src="https://cdn-icons-png.flaticon.com/128/18573/18573514.png"
                            alt="User Avatar"
                        />
                    </div>
                    <h1>Login</h1>
                </button>
            </header>

            <div className="pt-20">
                {showHeader && (
                    <Header
                        userName={userName}
                        fetchUserProfile={fetchAndHandleUserProfile}
                        handleLogout={handleLogout}
                    />
                )}

                <AddressContent
                    address={address}
                    isLoading={isLoading}
                    successMessage={successMessage}
                    showShareOptions={showShareOptions}
                    copiedField={copiedField}
                    currentOS={currentOS}
                    isAuthenticated={isAuthenticated}
                    handleDownload={handleDownload}
                    formatPamCode={formatPamCode}
                    formatPamCodeForCopy={formatPamCodeForCopy}
                    handleCopy={handleCopy}
                    HeartClick={HeartClick}
                    handleShareClick={handleShareClick}
                    handleLocationIconClick={handleLocationIconClick}
                    shareMessage={shareMessage}
                    t={t}
                />
            </div>

            <footer className="w-full mt-4 sm:mt-auto px-4">
                <div className="w-full px-6 py-6 bg-white shadow-shadow1 rounded-md">
                    <a
                        href="https://getmethere.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-center text-jetgrey text-sm hover:text-blue-600 transition-colors block"
                    >
                        GET ME THERE © {new Date().getFullYear()}
                    </a>
                </div>
            </footer>
        </div>
    );
};

export default AddressView;